import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import CatalogEditCreateHeader from '../../components/CatalogEditCreateHeader/CatalogEditCreateHeader';
import CatalogEditCreateFooter from '../../components/CatalogEditCreateFooter/CatalogEditCreateFooter';
import { Card, Drawer, Grid, Stack, Switch, Text, Title } from '@mantine/core';
import { IconFileDescription, IconReceipt } from '@tabler/icons-react';
import { useGetCashUpReportById } from '../../hooks/cashups/queries';
import moment from 'moment';
import { formatMoneyString, getCustomerFullName } from '../../utils/strings';
import { useReactToPrint } from 'react-to-print';
import classes from './Cashups.module.css';
import { useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import CatalogContainer from '../../components/CatalogContainer/CatalogContainer';
import { IInOutMovement } from '../../interfaces/Store';
import useCanUseMercadoPago from '../../hooks/useCanUseMercadoPago';
import { IOrder, IOrderPaymentLines } from '../../interfaces/Order';
import { buildOrderFolio } from '../../utils/ordersUtils';
import { useGetStoreById } from '../../hooks/stores/queries';
import { buildCashUpReportDisplaySummary } from '../../utils/cashUpReportSummary';
import DrawerActionCard from '../../components/DrawerActionCard';
import { tryPrintCashupReportEscPos } from '../../utils/cashupReportEscPos';

type CashupField = {
  span?: number;
  label: string | number | undefined;
  value?: string | number;
  className?: string;
};

type CashupDetailedLine = {
  number: number;
  type: string;
  paymentMethodLabel: string;
  inAmount: number;
  outAmount: number;
  customer: string;
  date: string;
  notes: string;
};

type CollectedOrderTicketLine = {
  folio: string;
  customer: string;
  collectedDate: string;
  total: string;
  paid: string;
  balance: string;
  paidInPeriod: string;
};

const getEntityName = (entity: any): string =>
  entity && typeof entity === 'object' && 'name' in entity ? entity.name || '' : '';

const formatCashupFieldValue = (value: number | string) =>
  typeof value === 'number' ? formatMoneyString(value) : value;

const CreatePage = () => {
  const { t } = useTranslation(['CashupsPage', 'FieldName', 'Notifications']);
  const history = useHistory();
  const location = useLocation<{ storeID?: string }>();
  const { id } = useParams<{ id: string }>();
  const fullReportPrintRef = useRef<HTMLDivElement>(null);
  const ticketPrintRef = useRef<HTMLDivElement>(null);
  const [printDrawerOpened, setPrintDrawerOpened] = useState(false);
  const [includeDetailedPrint, setIncludeDetailedPrint] = useState(true);
  const handlePrintFullReport = useReactToPrint({
    content: () => fullReportPrintRef.current,
    onAfterPrint: () => setPrintDrawerOpened(false)
  });
  const handlePrintTicket = useReactToPrint({
    content: () => ticketPrintRef.current,
  });
  const storeId = location?.state?.storeID;
  const { data: store } = useGetStoreById(!!(storeId || ''), storeId || '');
  const { data: reportData, isLoading: isGetLoading } = useGetCashUpReportById(
    storeId || '',
    id || ''
  );
  const { storeCanUseMP } = useCanUseMercadoPago(storeId || '');
  const reportSummary = useMemo(() => {
    if (!reportData) {
      return null;
    }

    return buildCashUpReportDisplaySummary(reportData);
  }, [reportData]);

  if (!storeId || !location.state || !location.state.storeID) {
    history.goBack();
    return <LoaderOverlay />;
  }

  const totalInOrders =
    (reportData?.cardFromOrders || 0) +
    +(reportData?.cashFromOrders || 0).toFixed(2) +
    +(reportData?.transferFromOrders || 0).toFixed(2) +
    +(reportData?.mercadoPagoFromOrders || 0).toFixed(2);

  const cashupBasicInfoFields = useMemo<CashupField[]>(() => {
    if (!reportData || !reportSummary) {
      return [];
    }
    const fields: CashupField[] = [
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.date'),
        value: moment(reportData.createdDate).format('DD/MM/YYYY')
      },
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.startingCash'),
        value: formatMoneyString(reportData.startingCash)
      },
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.hour'),
        value: moment(reportData.createdDate).format('HH:mm')
      },
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.user'),
        value: getEntityName(reportData.createdBy)
      },
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.calculated'),
        value: formatMoneyString(reportSummary.calculatedTotal)
      },
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.cashierBox'),
        value: getEntityName(reportData.cashierBox)
      },
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.difference'),
        value: formatMoneyString(reportSummary.difference)
      },
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.reportNumber'),
        value: reportData.reportNumber
      },
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.withdrawal'),
        value: formatMoneyString(reportData.cashToBank)
      },
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.notes'),
        value: reportData.notes || ''
      },
      {
        span: 6,
        label: t('FieldName:leftInDrawer'),
        value: formatMoneyString(reportData.leftInDrawer)
      }
    ];

    if (storeCanUseMP) {
      fields.push({
        span: 6,
        label: t('CashupsPage:cashUpReport.counted'),
        value: formatMoneyString(reportSummary.declaredTotal)
      });
    }

    return fields;
  }, [reportData, reportSummary, storeCanUseMP, t]);

  const cashupSalesDataField = useMemo<CashupField[]>(() => {
    if (!reportData) {
      return [];
    }
    const fields: CashupField[] = [
      {
        span: 12,
        label: t('CashupsPage:cashUpReport.paidCash'),
        value: formatMoneyString(reportData.cashFromOrders)
      },
      {
        span: 12,
        label: t('CashupsPage:cashUpReport.paidCard'),
        value: formatMoneyString(reportData.cardFromOrders)
      },
      {
        span: 12,
        label: t('CashupsPage:cashUpReport.paidTransfer'),
        value: formatMoneyString(reportData.transferFromOrders)
      },
      {
        span: 12,
        label: t('CashupsPage:cashUpReport.paidTotal'),
        value: formatMoneyString(
          reportData.cashFromOrders +
            reportData.cardFromOrders +
            (reportData?.transferFromOrders || 0) +
            (reportData?.mercadoPagoFromOrders || 0)
        )
      }
    ];

    if (storeCanUseMP) {
      fields.splice(3, 0, {
        span: 12,
        label: t('CashupsPage:cashUpReport.paidMercadoPago'),
        value: formatMoneyString(reportData.mercadoPagoFromOrders)
      });
    }

    return fields;
  }, [reportData, storeCanUseMP, t]);

  const cashupCashierBoxMovements = useMemo(
    () =>
      reportData
        ? [
            {
              span: 6,
              label: t('CashupsPage:cashUpReport.ins'),
              value: formatMoneyString(
                reportData.cashPayIns +
                  reportData.cardPayIns +
                  (reportData.transferPayIns || 0) +
                  (reportData.mercadoPagoPayIns || 0)
              )
            },
            {
              span: 6,
              label: t('CashupsPage:cashUpReport.outs'),
              value: formatMoneyString(
                reportData.cashPayOuts +
                  reportData.cardPayOuts +
                  (reportData.transferPayOuts || 0) +
                  (reportData.mercadoPagoPayOuts || 0)
              )
            }
          ]
        : [],
    [reportData]
  );

  const cashupGeneralMovements = useMemo(
    () =>
      reportData
        ? [
            {
              span: 6,
              label: t('CashupsPage:cashUpReport.ordersIns'),
              value: formatMoneyString(
                (
                  +reportData.cardFromOrders +
                  +reportData.cashFromOrders +
                  +(reportData?.transferFromOrders || 0) +
                  +(reportData?.mercadoPagoFromOrders || 0)
                ).toFixed(2)
              )
            },
            {
              span: 6,
              label: t('CashupsPage:cashUpReport.cashierBoxOuts'),
              value: formatMoneyString(
                reportData.cashPayOuts +
                  reportData.cardPayOuts +
                  (reportData.transferPayOuts || 0) +
                  (reportData.mercadoPagoPayOuts || 0)
              )
            },
            {
              span: 12,
              label: t('CashupsPage:cashUpReport.cashierBoxIns'),
              value: formatMoneyString(
                reportData.cashPayIns +
                  reportData.cardPayIns +
                  (reportData.transferPayIns || 0) +
                  (reportData.mercadoPagoPayIns || 0)
              )
            },
            {
              span: 6,
              label: t('CashupsPage:cashUpReport.total'),
              value: formatMoneyString(
                totalInOrders +
                  reportData.cashPayIns +
                  reportData.cardPayIns +
                  (reportData.transferPayIns || 0) +
                  (reportData.mercadoPagoPayIns || 0)
              )
            },
            {
              span: 6,
              label: t('CashupsPage:cashUpReport.total'),
              value: formatMoneyString(
                reportData.cashPayOuts +
                  reportData.cardPayOuts +
                  (reportData.transferPayOuts || 0) +
                  (reportData.mercadoPagoPayOuts || 0)
              )
            }
          ]
        : [],
    [reportData]
  );

  const cashupMovementPaymentType = useMemo<CashupField[]>(() => {
    if (!reportSummary) {
      return [];
    }
    const paymentTypeRows = reportSummary.paymentTypeRows.filter(
      (row) => row.key !== 'mercadopago' || storeCanUseMP
    );
    const fields: CashupField[] = [
      //Titles
      { span: 3, label: t('CashupsPage:cashUpReport.type'), className: classes.label },
      { span: 3, label: t('CashupsPage:cashUpReport.calculated'), className: classes.label },
      { span: 3, label: t('CashupsPage:cashUpReport.counted'), className: classes.label },
      { span: 3, label: t('CashupsPage:cashUpReport.difference'), className: classes.label },
    ];

    paymentTypeRows.forEach((row) => {
      fields.push(
        { span: 3, label: t(`CashupsPage:cashUpReport.${row.key}`) },
        { span: 3, label: formatMoneyString(row.calculated) },
        { span: 3, label: formatCashupFieldValue(row.counted) },
        { span: 3, label: formatCashupFieldValue(row.difference) }
      );
    });

    return fields;
  }, [reportSummary, storeCanUseMP, t]);

  const cashupMovementPaymentTypeRows = useMemo(() => {
    if (!reportSummary) {
      return [];
    }

    return reportSummary.paymentTypeRows
      .filter((row) => row.key !== 'mercadopago' || storeCanUseMP)
      .map((row) => ({
        type: t(`CashupsPage:cashUpReport.${row.key}`),
        calculated: formatMoneyString(row.calculated),
        counted: formatCashupFieldValue(row.counted),
        difference: formatCashupFieldValue(row.difference)
      }));
  }, [reportSummary, storeCanUseMP, t]);

  const cashupDetailedRows = useMemo<CashupDetailedLine[]>(() => {
    if (!reportData) {
      return [];
    }

    return (reportData.lines || []).map((line, idx) => {
      let type = '';
      let date = '';
      let notes = '';
      let customer = '';

      if (line.order) {
        type = buildOrderFolio(
          (line.order as IOrder)?.folioPrefix,
          (line.order as IOrder)?.sequence
        );
        const paymentLine = (line?.order as IOrder)?.paymentLines?.find(
          (pl: IOrderPaymentLines) => String(pl._id) === String(line.orderPaymentLineId)
        );
        date = moment(paymentLine?.paymentDate).format('DD/MM/YYYY');
        customer = getCustomerFullName(
          (line?.order as any)?.customerName,
          (line?.order as any)?.customerLastName
        );
      }

      if (line.movement) {
        type = t('CashupsPage:cashUpReport.movement');
        date = moment((line?.movement as IInOutMovement)?.date).format('DD/MM/YYYY');
        notes = (line?.movement as IInOutMovement)?.notes || '';
      }

      return {
        number: idx + 1,
        type,
        paymentMethodLabel: t(`CashupsPage:cashUpReport.paymentMethods.${line.paymentMethod}`),
        inAmount: line.type === 'in' ? line.amount : 0,
        outAmount: line.type === 'out' ? line.amount : 0,
        customer,
        date,
        notes
      };
    });
  }, [reportData, t]);

  const cashupDetailedLines = useMemo(
    () =>
      reportData
        ? [
            //Titles
            { span: 1, label: '#', className: classes.label },
            { span: 2, label: t('CashupsPage:cashUpReport.type'), className: classes.label },
            { span: 2, label: t('CashupsPage:cashUpReport.method'), className: classes.label },
            { span: 1, label: t('CashupsPage:cashUpReport.in'), className: classes.label },
            { span: 1, label: t('CashupsPage:cashUpReport.out'), className: classes.label },
            { span: 2, label: t('CashupsPage:cashUpReport.customer'), className: classes.label },
            { span: 1, label: t('CashupsPage:cashUpReport.date'), className: classes.label },
            { span: 2, label: t('CashupsPage:cashUpReport.notes'), className: classes.label },
            //Values
            ...cashupDetailedRows.reduce((prev, line) => {
              prev.push(
                { span: 1, label: line.number },
                { span: 2, label: line.type },
                { span: 2, label: line.paymentMethodLabel },
                { span: 1, label: formatMoneyString(line.inAmount) },
                { span: 1, label: formatMoneyString(line.outAmount) },
                { span: 2, label: line.customer },
                { span: 1, label: line.date },
                { span: 2, label: line.notes }
              );
              return prev;
            }, [] as { span: number; label: string | number | undefined; className?: any }[]),
            { span: 5, label: t('CashupsPage:cashUpReport.total'), className: classes.totalLabel },
            {
              span: 1,
              label: formatMoneyString(
                reportData.cardFromOrders +
                  reportData.cashFromOrders +
                  (reportData.transferFromOrders || 0) +
                  reportData.cashPayIns +
                  (reportData.transferPayIns || 0) +
                  reportData.cardPayIns +
                  (reportData.mercadoPagoPayIns || 0) +
                  (reportData?.mercadoPagoFromOrders || 0)
              ),
              className: classes.totalLabel
            },
            {
              span: 1,
              label: formatMoneyString(
                reportData.cashPayOuts +
                  reportData.cardPayOuts +
                  (reportData.transferPayOuts || 0) +
                  (reportData?.mercadoPagoPayOuts || 0)
              ),
              className: classes.totalLabel
            }
          ]
        : [],
    [cashupDetailedRows, reportData, t]
  );

  const collectedOrdersSection = useMemo(() => {
    if (!reportData) {
      return null;
    }

    const periodFromLabel = reportData.periodFrom
      ? moment(reportData.periodFrom).format('DD/MM/YYYY HH:mm')
      : '';
    const periodToLabel = reportData.periodTo
      ? moment(reportData.periodTo).format('DD/MM/YYYY HH:mm')
      : '';

    const rows: { key: string; span: number; label: string | number; className?: string }[] = [
      { key: 'co-header-folio', span: 2, label: 'Folio', className: classes.label },
      { key: 'co-header-customer', span: 3, label: 'Cliente', className: classes.label },
      { key: 'co-header-date', span: 2, label: 'Entrega', className: classes.label },
      { key: 'co-header-total', span: 1, label: 'Total', className: classes.label },
      { key: 'co-header-paid', span: 1, label: 'Pagado', className: classes.label },
      { key: 'co-header-balance', span: 1, label: 'Saldo', className: classes.label },
      {
        key: 'co-header-paid-period',
        span: 2,
        label: 'Pagado en periodo',
        className: classes.label
      }
    ];

    const summaries = reportData.collectedOrdersSummary || [];
    const totals = summaries.reduce(
      (acc, summary) => {
        const balance = Math.max((summary?.total || 0) - (summary?.amountPaid || 0), 0);
        return {
          total: acc.total + (summary?.total || 0),
          paidInPeriod: acc.paidInPeriod + (summary?.paidInPeriod || 0),
          balance: acc.balance + balance
        };
      },
      { total: 0, paidInPeriod: 0, balance: 0 }
    );

    const ticketRows: CollectedOrderTicketLine[] = [];
    const emptyLabel = 'Sin servicios concluidos en el periodo';

    if (summaries.length === 0) {
      rows.push({
        key: 'co-empty',
        span: 12,
        label: emptyLabel
      });
    } else {
      summaries.forEach((summary, idx) => {
        const balance = Math.max((summary?.total || 0) - (summary?.amountPaid || 0), 0);
        const folio = buildOrderFolio(summary?.folioPrefix, summary?.sequence) || '';
        const customer = `${summary?.customerName || ''} ${summary?.customerLastName || ''}`.trim();
        const collectedDate = summary?.collectedDateTime
          ? moment(summary.collectedDateTime).format('DD/MM/YYYY HH:mm')
          : '';
        const total = formatMoneyString(summary?.total || 0) || '';
        const paid = formatMoneyString(summary?.amountPaid || 0) || '';
        const balanceLabel = formatMoneyString(balance) || '';
        const paidInPeriod = formatMoneyString(summary?.paidInPeriod || 0) || '';

        ticketRows.push({
          folio,
          customer,
          collectedDate,
          total,
          paid,
          balance: balanceLabel,
          paidInPeriod
        });

        rows.push(
          {
            key: `co-${idx}-folio`,
            span: 2,
            label: folio
          },
          {
            key: `co-${idx}-customer`,
            span: 3,
            label: customer
          },
          {
            key: `co-${idx}-date`,
            span: 2,
            label: collectedDate
          },
          {
            key: `co-${idx}-total`,
            span: 1,
            label: total
          },
          {
            key: `co-${idx}-paid`,
            span: 1,
            label: paid
          },
          {
            key: `co-${idx}-balance`,
            span: 1,
            label: balanceLabel
          },
          {
            key: `co-${idx}-paid-period`,
            span: 2,
            label: paidInPeriod
          }
        );
      });
    }

    const totalsLabel = `Total servicios: ${summaries.length}${
      summaries.length > 0
        ? `  |  Total: ${formatMoneyString(
            totals.total
          )}  |  Pagado en periodo: ${formatMoneyString(
            totals.paidInPeriod
          )}  |  Saldo total: ${formatMoneyString(totals.balance)}`
        : ''
    }`;

    const periodLabel =
      periodFromLabel && periodToLabel ? `Periodo: ${periodFromLabel} - ${periodToLabel}` : '';

    return {
      rows,
      ticketRows,
      totalsLabel,
      periodLabel,
      emptyLabel
    };
  }, [reportData]);

  const ticketValue = (value: string | number | undefined) =>
    value === undefined || value === '' ? '--' : value;

  const cancellationAuditFields = useMemo<CashupField[]>(() => {
    if (!reportData || reportData.isActive !== false) {
      return [];
    }

    const fields: CashupField[] = [
      {
        span: 6,
        label: t('CashupsPage:cashUpReport.cancellation.status'),
        value: t('CashupsPage:cashUpReport.cancellation.cancelled')
      }
    ];

    if (reportData.cancelledAt) {
      fields.push({
        span: 6,
        label: t('CashupsPage:cashUpReport.cancellation.cancelledAt'),
        value: moment(reportData.cancelledAt).format('DD/MM/YYYY HH:mm')
      });
    }

    const cancelledByName = getEntityName(reportData.cancelledBy);
    if (cancelledByName) {
      fields.push({
        span: 6,
        label: t('CashupsPage:cashUpReport.cancellation.cancelledBy'),
        value: cancelledByName
      });
    }

    if (reportData.cancellationReason) {
      fields.push({
        span: 12,
        label: t('CashupsPage:cashUpReport.cancellation.reason'),
        value: reportData.cancellationReason
      });
    }

    return fields;
  }, [reportData, t]);

  const renderFullReport = (
    reportRef: RefObject<HTMLDivElement> | null,
    includeDetailed: boolean,
    detailSectionTestId: string
  ) => (
    <Card ref={reportRef || undefined}>
      <Card.Section inheritPadding withBorder py="md">
        <Grid>
          <Grid.Col span={12} className={classes.titleRow}>
            <Title className={classes.title} ta="center" order={4}>
              {t('CashupsPage:cashUpReport.title')}
            </Title>
          </Grid.Col>
          <Grid.Col span={12}>
            <Grid py="xs">
              {cashupBasicInfoFields.map((field, idx) => (
                <Grid.Col p={0} span={field.span || 6} key={`cashupBasicInfoFields-${idx}`}>
                  <Text className={classes.label} display={'inline'}>
                    {field.label}:
                  </Text>{' '}
                  <Text display={'inline'}>{field.value}</Text>
                </Grid.Col>
              ))}
            </Grid>
          </Grid.Col>
          {cancellationAuditFields.length > 0 && (
            <>
              <Grid.Col span={12} className={classes.titleRow}>
                <Title className={classes.title} ta="center" order={4}>
                  {t('CashupsPage:cashUpReport.cancellation.title')}
                </Title>
              </Grid.Col>
              <Grid.Col span={12} data-testid="cashup-cancellation-audit">
                <Grid py="xs">
                  {cancellationAuditFields.map((field, idx) => (
                    <Grid.Col p={0} span={field.span || 6} key={`cancellationAuditFields-${idx}`}>
                      <Text className={classes.label}>{field.label}</Text>
                      <Text>{field.value}</Text>
                    </Grid.Col>
                  ))}
                </Grid>
              </Grid.Col>
            </>
          )}
          <Grid.Col span={12} className={classes.titleRow}>
            <Title className={classes.title} ta="center" order={4}>
              {t('CashupsPage:cashUpReport.salesDataTitle')}
            </Title>
          </Grid.Col>
          <Grid.Col span={12}>
            <Grid py="xs">
              {cashupSalesDataField.map((field, idx) => (
                <Grid.Col p={0} span={field.span || 6} key={`cashupSalesDataField-${idx}`}>
                  <Text className={classes.label} display={'inline'}>
                    {field.label}:
                  </Text>{' '}
                  <Text display={'inline'}>{field.value}</Text>
                </Grid.Col>
              ))}
            </Grid>
          </Grid.Col>
          <Grid.Col span={12} className={classes.titleRow}>
            <Title className={classes.title} ta="center" order={4}>
              {t('CashupsPage:cashUpReport.cashierBoxMovementTitle')}
            </Title>
          </Grid.Col>
          <Grid.Col span={12}>
            <Grid py="xs">
              {cashupCashierBoxMovements.map((field, idx) => (
                <Grid.Col span={field.span || 6} key={`cashupCashierBoxMovements-${idx}`}>
                  <Text className={classes.label} display={'inline'}>
                    {field.label}:
                  </Text>{' '}
                  <Text display={'inline'}>{field.value}</Text>
                </Grid.Col>
              ))}
            </Grid>
          </Grid.Col>
          <Grid.Col span={12} className={classes.titleRow}>
            <Title className={classes.title} ta="center" order={4}>
              {t('CashupsPage:cashUpReport.generalMovementsTitle')}
            </Title>
          </Grid.Col>
          <Grid.Col span={12}>
            <Grid py="xs">
              {cashupGeneralMovements.map((field, idx) => (
                <Grid.Col p={0} span={field.span || 6} key={`cashupGeneralMovements-${idx}`}>
                  <Text className={classes.label} display={'inline'}>
                    {field.label}:
                  </Text>{' '}
                  <Text display={'inline'}>{field.value}</Text>
                </Grid.Col>
              ))}
            </Grid>
          </Grid.Col>
          <Grid.Col span={12} className={classes.titleRow}>
            <Title className={classes.title} ta="center" order={4}>
              {t('CashupsPage:cashUpReport.movementPaymentTypeTitle')}
            </Title>
          </Grid.Col>
          <Grid.Col span={12}>
            <Grid py="xs">
              {cashupMovementPaymentType.map((field, idx) => (
                <Grid.Col p={0} span={field.span || 6} key={`cashupMovementPaymentType-${idx}`}>
                  <Text className={field.className || ''} display={'inline'}>
                    {field.label}
                  </Text>
                </Grid.Col>
              ))}
            </Grid>
          </Grid.Col>
          {includeDetailed && (
            <>
              <Grid.Col span={12} className={classes.titleRow}>
                <Title className={classes.title} ta="center" order={4}>
                  {t('CashupsPage:cashUpReport.detailedTitle')}
                </Title>
              </Grid.Col>
              <Grid.Col span={12} data-testid={detailSectionTestId}>
                <Grid py="xs">
                  {cashupDetailedLines.map((field, idx) => (
                    <Grid.Col p={0} span={field.span || 6} key={`cashupDetailedLines-${idx}`}>
                      <Text className={field.className || ''} display={'inline'}>
                        {field.label}
                      </Text>
                    </Grid.Col>
                  ))}
                </Grid>
              </Grid.Col>
            </>
          )}
          {store?.cashUpReportConfig?.showCollectedOrdersSection && (
            <>
              <Grid.Col span={12} className={classes.titleRow}>
                <Title className={classes.title} ta="center" order={4}>
                  Servicios concluidos (entregados)
                </Title>
              </Grid.Col>
              {collectedOrdersSection && (
                <>
                  {collectedOrdersSection.periodLabel ? (
                    <Grid.Col p={0} span={12}>
                      <Text className={classes.label}>{collectedOrdersSection.periodLabel}</Text>
                    </Grid.Col>
                  ) : null}
                  <Grid.Col span={12}>
                    <Grid py="xs">
                      {collectedOrdersSection.rows.map((field) => (
                        <Grid.Col p={0} span={field.span || 6} key={field.key}>
                          <Text className={field.className || ''} display={'inline'}>
                            {field.label}
                          </Text>
                        </Grid.Col>
                      ))}
                    </Grid>
                  </Grid.Col>
                  {collectedOrdersSection.totalsLabel ? (
                    <Grid.Col p={0} span={12}>
                      <Text className={classes.label}>{collectedOrdersSection.totalsLabel}</Text>
                    </Grid.Col>
                  ) : null}
                </>
              )}
            </>
          )}
        </Grid>
      </Card.Section>
    </Card>
  );

  const renderTicketFieldRows = (fields: CashupField[]) => (
    <div className={classes.ticketFieldList}>
      {fields.map((field, idx) => (
        <div className={classes.ticketFieldRow} key={`ticket-field-${field.label}-${idx}`}>
          <span className={classes.ticketFieldLabel}>{field.label}:</span>{' '}
          <span>{ticketValue(field.value)}</span>
        </div>
      ))}
    </div>
  );

  const renderTicketSection = (title: string, children: ReactNode) => (
    <section className={classes.ticketSection}>
      <Text className={classes.ticketSectionTitle}>{title}</Text>
      {children}
    </section>
  );

  const renderTicketDetailField = (label: string, value: string | number | undefined) => (
    <div className={classes.ticketDetailField}>
      <span className={classes.ticketFieldLabel}>{label}:</span> <span>{ticketValue(value)}</span>
    </div>
  );

  const renderTicketReport = (reportRef: RefObject<HTMLDivElement>) => (
    <div ref={reportRef} className={classes.ticketReport}>
      <Title ta="center" order={5}>
        {t('CashupsPage:cashUpReport.title')}
      </Title>
      {renderTicketSection(
        t('CashupsPage:cashUpReport.print.basicInfoTitle'),
        renderTicketFieldRows(cashupBasicInfoFields)
      )}
      {renderTicketSection(
        t('CashupsPage:cashUpReport.salesDataTitle'),
        renderTicketFieldRows(cashupSalesDataField)
      )}
      {renderTicketSection(
        t('CashupsPage:cashUpReport.cashierBoxMovementTitle'),
        renderTicketFieldRows(cashupCashierBoxMovements)
      )}
      {renderTicketSection(
        t('CashupsPage:cashUpReport.generalMovementsTitle'),
        renderTicketFieldRows(cashupGeneralMovements)
      )}
      {renderTicketSection(
        t('CashupsPage:cashUpReport.movementPaymentTypeTitle'),
        <div className={classes.ticketFieldList}>
          {cashupMovementPaymentTypeRows.map((row, idx) => (
            <div className={classes.ticketPaymentTypeBlock} key={`ticket-payment-type-${idx}`}>
              <Text fw={600}>{row.type}</Text>
              {renderTicketDetailField(t('CashupsPage:cashUpReport.calculated'), row.calculated)}
              {renderTicketDetailField(t('CashupsPage:cashUpReport.counted'), row.counted)}
              {renderTicketDetailField(t('CashupsPage:cashUpReport.difference'), row.difference)}
            </div>
          ))}
        </div>
      )}
      {includeDetailedPrint &&
        renderTicketSection(
          t('CashupsPage:cashUpReport.detailedTitle'),
          <div className={classes.ticketDetailList}>
            {cashupDetailedRows.map((line, idx) => (
              <div
                className={classes.ticketDetailBlock}
                data-testid={`cashup-ticket-detail-line-${idx}`}
                key={`ticket-detail-${line.number}`}>
                <Text fw={700}>
                  {line.number}) {ticketValue(line.type)}
                </Text>
                {renderTicketDetailField(t('CashupsPage:cashUpReport.customer'), line.customer)}
                {renderTicketDetailField(t('CashupsPage:cashUpReport.method'), line.paymentMethodLabel)}
                {renderTicketDetailField(
                  t('CashupsPage:cashUpReport.in'),
                  formatMoneyString(line.inAmount)
                )}
                {renderTicketDetailField(
                  t('CashupsPage:cashUpReport.out'),
                  formatMoneyString(line.outAmount)
                )}
                {renderTicketDetailField(t('CashupsPage:cashUpReport.date'), line.date)}
                {renderTicketDetailField(t('CashupsPage:cashUpReport.notes'), line.notes)}
              </div>
            ))}
          </div>
        )}
      {store?.cashUpReportConfig?.showCollectedOrdersSection &&
        collectedOrdersSection &&
        renderTicketSection(
          'Servicios concluidos (entregados)',
          <>
            {collectedOrdersSection.periodLabel ? (
              <Text className={classes.ticketFieldLabel}>{collectedOrdersSection.periodLabel}</Text>
            ) : null}
            {collectedOrdersSection.ticketRows.length > 0 ? (
              <div className={classes.ticketDetailList}>
                {collectedOrdersSection.ticketRows.map((line, idx) => (
                  <div
                    className={classes.ticketCollectedOrderBlock}
                    data-testid={`cashup-ticket-collected-order-${idx}`}
                    key={`ticket-collected-order-${idx}`}>
                    {renderTicketDetailField('Folio', line.folio)}
                    {renderTicketDetailField('Cliente', line.customer)}
                    {renderTicketDetailField('Entrega', line.collectedDate)}
                    {renderTicketDetailField('Total', line.total)}
                    {renderTicketDetailField('Pagado', line.paid)}
                    {renderTicketDetailField('Saldo', line.balance)}
                    {renderTicketDetailField('Pagado en periodo', line.paidInPeriod)}
                  </div>
                ))}
              </div>
            ) : (
              <div className={classes.ticketFieldList}>
                <div className={classes.ticketFieldRow}>
                  <span>{collectedOrdersSection.emptyLabel}</span>
                </div>
              </div>
            )}
            {collectedOrdersSection.totalsLabel ? (
              <Text className={classes.ticketFieldLabel}>{collectedOrdersSection.totalsLabel}</Text>
            ) : null}
          </>
        )}
    </div>
  );

  const onPrintFullReport = () => {
    handlePrintFullReport();
  };

  const onPrintTicket = async () => {
    if (!reportData) {
      handlePrintTicket();
      return;
    }

    const printedWithEscPos = await tryPrintCashupReportEscPos({
      reportData,
      store,
      storeCanUseMP,
      includeDetailed: includeDetailedPrint,
      t,
    });

    if (printedWithEscPos) {
      setPrintDrawerOpened(false);
      return;
    }

    handlePrintTicket();
  };

  if (isGetLoading && !reportData) {
    return <LoaderOverlay />;
  }

  return (
    <CatalogContainer>
      <CatalogEditCreateHeader title={t('CashupsPage:title')} />
      {renderFullReport(null, true, 'cashup-visible-detail-section')}
      <Drawer
        opened={printDrawerOpened}
        onClose={() => setPrintDrawerOpened(false)}
        position="right"
        title={t('CashupsPage:cashUpReport.print.title')}
        padding="md"
        size="sm">
        <Stack gap="sm">
          <Switch
            checked={includeDetailedPrint}
            label={t('CashupsPage:cashUpReport.print.includeDetailed')}
            onChange={(event) => setIncludeDetailedPrint(event.currentTarget.checked)}
          />
          <DrawerActionCard
            label={t('CashupsPage:cashUpReport.print.ticket')}
            description={t('CashupsPage:cashUpReport.print.ticketDescription')}
            icon={<IconReceipt />}
            iconColor="teal"
            onClick={onPrintTicket}
            ariaLabel={t('CashupsPage:cashUpReport.print.ticket')}
          />
          <DrawerActionCard
            label={t('CashupsPage:cashUpReport.print.fullReport')}
            description={t('CashupsPage:cashUpReport.print.fullReportDescription')}
            icon={<IconFileDescription />}
            iconColor="blue"
            onClick={onPrintFullReport}
            ariaLabel={t('CashupsPage:cashUpReport.print.fullReport')}
          />
        </Stack>
      </Drawer>
      {printDrawerOpened && (
        <div className={classes.printOnlyHost} aria-hidden="true">
          {renderFullReport(
            fullReportPrintRef,
            includeDetailedPrint,
            'cashup-full-print-detail-section'
          )}
          {renderTicketReport(ticketPrintRef)}
        </div>
      )}
      <CatalogEditCreateFooter
        loading={isGetLoading}
        hideSubmit
        showPrint
        onPrint={() => setPrintDrawerOpened(true)}
      />
    </CatalogContainer>
  );
};

export default CreatePage;
